import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Briefcase,
  CheckCircle2,
  GraduationCap,
  Loader2,
  Rocket,
  Sparkles,
} from 'lucide-react';
import { submitInternApplication } from '../lib/careers/api';
import {
  AVAILABILITY_OPTIONS,
  INTERN_TRACKS,
  emptyInternApplication,
  type InternApplication,
  type InternTrackId,
} from '../lib/careers/types';
import { getInternApplicationErrors } from '../lib/careers/validateApplication';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

const INPUT_CLASS =
  'mt-1 w-full rounded-xl border border-white/15 bg-black px-4 py-3 text-white placeholder:text-white/35 focus:border-indigo-400/60 focus:outline-none focus:ring-1 focus:ring-indigo-400/40';

const SELECT_CLASS = `${INPUT_CLASS} [color-scheme:dark]`;

export default function CareersPage() {
  const [application, setApplication] = useState<InternApplication>(emptyInternApplication);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = <K extends keyof InternApplication>(
    key: K,
    value: InternApplication[K]
  ) => {
    setApplication((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    setFormError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = getInternApplicationErrors(application);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    setFormError(null);

    try {
      await submitInternApplication(application);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : 'Could not submit application. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>SELF Labs Fellowship — Careers</title>
        <meta
          name="description"
          content="Apply to the SELF Labs Fellowship: an unpaid internship for students and early-career builders working on AI, robotics, BCI, and conscious technology."
        />
      </Helmet>

      <div
        id="careers"
        data-avatar-target="self-labs"
        className="relative min-h-screen bg-black text-white"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-indigo-600/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-purple-600/15 blur-3xl" />
        </div>

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/"
                className="group inline-flex items-center text-sm text-white/50 transition-colors hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                Back to Home
              </Link>
            </motion.div>

            {submitted ? (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-auto max-w-2xl py-16 text-center"
              >
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-500/10">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h1 className="text-3xl font-bold sm:text-4xl">Application received</h1>
                <p className="mt-4 text-lg text-white/65">
                  Thanks for applying to the SELF Labs Fellowship. We review every application and
                  will follow up at <span className="text-white">{application.email}</span> if there
                  is a fit for the current cohort.
                </p>
                <p className="mt-3 text-sm text-white/45">
                  Questions? Email{' '}
                  <a href="mailto:careers@selflabs.xyz" className="text-indigo-400 hover:text-indigo-300">
                    careers@selflabs.xyz
                  </a>
                </p>
                <Link
                  to="/about"
                  className="mt-8 inline-flex items-center rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-white/10"
                >
                  Learn more about SELF Labs
                </Link>
              </motion.section>
            ) : (
              <>
                <section className="mx-auto max-w-3xl pb-14 pt-10 text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                      <Sparkles className="h-3.5 w-3.5" />
                      SELF Labs Careers
                    </span>
                    <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                      SELF Labs Fellowship
                      <span className="mt-2 block text-2xl font-medium text-white/55 sm:text-3xl">
                        (Internship)
                      </span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                      An unpaid internship program for students and early-career builders, with
                      opportunities to transition into paid contractor or full-time roles based on
                      performance and company growth.
                    </p>
                    <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/50">
                      SELF Labs is building conscious systems — AI avatars, signal-native OS layers,
                      robotics, and BCI. We are looking for curious fellows who want real ownership on
                      frontier work, not busywork.
                    </p>
                  </motion.div>
                </section>

                <section className="border-t border-white/10 py-14">
                  <motion.div {...fadeUp} className="mx-auto max-w-5xl">
                    <div className="mb-8 flex items-center gap-3">
                      <Rocket className="h-5 w-5 text-indigo-400" />
                      <h2 className="text-2xl font-semibold">What you will work on</h2>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        'Ship features used on selflabs.xyz and partner products',
                        'Prototype AI agents, avatars, and signal workflows',
                        'Collaborate directly with founder Milan Cheeks',
                        'Learn full-stack, serverless, and product thinking in production',
                        'Optional exposure to BCI, robotics, and hardware-adjacent R&D',
                        'Small team — your contributions are visible from day one',
                      ].map((item) => (
                        <div
                          key={item}
                          className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm leading-relaxed text-white/70"
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </section>

                <section className="border-t border-white/10 py-14">
                  <motion.div {...fadeUp} className="mx-auto max-w-5xl">
                    <div className="mb-8 flex items-center gap-3">
                      <Briefcase className="h-5 w-5 text-indigo-400" />
                      <h2 className="text-2xl font-semibold">Fellowship tracks</h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {INTERN_TRACKS.map((track) => (
                        <button
                          key={track.id}
                          type="button"
                          onClick={() => updateField('track', track.id as InternTrackId)}
                          className={`rounded-2xl border p-6 text-left transition ${
                            application.track === track.id
                              ? 'border-indigo-400/50 bg-indigo-500/10'
                              : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                          }`}
                        >
                          <h3 className="text-lg font-semibold">{track.title}</h3>
                          <p className="mt-2 text-sm leading-relaxed text-white/60">
                            {track.summary}
                          </p>
                          <ul className="mt-4 flex flex-wrap gap-2">
                            {track.focus.map((tag) => (
                              <li
                                key={tag}
                                className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-xs text-white/55"
                              >
                                {tag}
                              </li>
                            ))}
                          </ul>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </section>

                <section className="border-t border-white/10 py-14">
                  <motion.div {...fadeUp} className="mx-auto max-w-3xl">
                    <div className="mb-8 flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-indigo-400" />
                      <h2 className="text-2xl font-semibold">Apply to the Fellowship</h2>
                    </div>

                    <form
                      onSubmit={handleSubmit}
                      className="rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8"
                    >
                      {formError && (
                        <div className="mb-6 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                          {formError}
                        </div>
                      )}

                      <div className="grid gap-5 sm:grid-cols-2">
                        <label className="block text-sm">
                          <span className="text-white/70">First name *</span>
                          <input
                            className={INPUT_CLASS}
                            value={application.firstName}
                            onChange={(e) => updateField('firstName', e.target.value)}
                            autoComplete="given-name"
                          />
                          {errors.firstName && (
                            <span className="mt-1 block text-xs text-red-300">{errors.firstName}</span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Last name *</span>
                          <input
                            className={INPUT_CLASS}
                            value={application.lastName}
                            onChange={(e) => updateField('lastName', e.target.value)}
                            autoComplete="family-name"
                          />
                          {errors.lastName && (
                            <span className="mt-1 block text-xs text-red-300">{errors.lastName}</span>
                          )}
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">Email *</span>
                          <input
                            type="email"
                            className={INPUT_CLASS}
                            value={application.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            autoComplete="email"
                          />
                          {errors.email && (
                            <span className="mt-1 block text-xs text-red-300">{errors.email}</span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Phone</span>
                          <input
                            type="tel"
                            className={INPUT_CLASS}
                            value={application.phone}
                            onChange={(e) => updateField('phone', e.target.value)}
                            autoComplete="tel"
                          />
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Fellowship track *</span>
                          <select
                            className={SELECT_CLASS}
                            value={application.track}
                            onChange={(e) => updateField('track', e.target.value as InternTrackId)}
                          >
                            {INTERN_TRACKS.map((track) => (
                              <option key={track.id} value={track.id}>
                                {track.title}
                              </option>
                            ))}
                          </select>
                          {errors.track && (
                            <span className="mt-1 block text-xs text-red-300">{errors.track}</span>
                          )}
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">School or university *</span>
                          <input
                            className={INPUT_CLASS}
                            value={application.school}
                            onChange={(e) => updateField('school', e.target.value)}
                          />
                          {errors.school && (
                            <span className="mt-1 block text-xs text-red-300">{errors.school}</span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Major / field *</span>
                          <input
                            className={INPUT_CLASS}
                            value={application.major}
                            onChange={(e) => updateField('major', e.target.value)}
                          />
                          {errors.major && (
                            <span className="mt-1 block text-xs text-red-300">{errors.major}</span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Expected graduation year *</span>
                          <input
                            className={INPUT_CLASS}
                            inputMode="numeric"
                            placeholder="2027"
                            value={application.graduationYear}
                            onChange={(e) => updateField('graduationYear', e.target.value)}
                          />
                          {errors.graduationYear && (
                            <span className="mt-1 block text-xs text-red-300">
                              {errors.graduationYear}
                            </span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Availability *</span>
                          <select
                            className={SELECT_CLASS}
                            value={application.availability}
                            onChange={(e) =>
                              updateField(
                                'availability',
                                e.target.value as InternApplication['availability']
                              )
                            }
                          >
                            {AVAILABILITY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {errors.availability && (
                            <span className="mt-1 block text-xs text-red-300">
                              {errors.availability}
                            </span>
                          )}
                        </label>

                        <label className="block text-sm">
                          <span className="text-white/70">Preferred start</span>
                          <input
                            className={INPUT_CLASS}
                            placeholder="e.g. June 2026"
                            value={application.preferredStart}
                            onChange={(e) => updateField('preferredStart', e.target.value)}
                          />
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">LinkedIn URL</span>
                          <input
                            type="url"
                            className={INPUT_CLASS}
                            placeholder="https://linkedin.com/in/..."
                            value={application.linkedinUrl}
                            onChange={(e) => updateField('linkedinUrl', e.target.value)}
                          />
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">Portfolio or GitHub URL</span>
                          <input
                            type="url"
                            className={INPUT_CLASS}
                            placeholder="https://github.com/..."
                            value={application.portfolioUrl}
                            onChange={(e) => updateField('portfolioUrl', e.target.value)}
                          />
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">Resume link</span>
                          <input
                            type="url"
                            className={INPUT_CLASS}
                            placeholder="Google Drive, Notion, or personal site link"
                            value={application.resumeUrl}
                            onChange={(e) => updateField('resumeUrl', e.target.value)}
                          />
                          <span className="mt-1 block text-xs text-white/40">
                            Share a viewable link to your resume (PDF preferred).
                          </span>
                        </label>

                        <label className="block text-sm sm:col-span-2">
                          <span className="text-white/70">Why SELF Labs? *</span>
                          <textarea
                            rows={5}
                            className={`${INPUT_CLASS} resize-y`}
                            placeholder="What draws you to the fellowship, conscious AI, and the track you selected?"
                            value={application.whySelfLabs}
                            onChange={(e) => updateField('whySelfLabs', e.target.value)}
                          />
                          <span className="mt-1 block text-xs text-white/40">
                            {application.whySelfLabs.trim().length} / 40 minimum characters
                          </span>
                          {errors.whySelfLabs && (
                            <span className="mt-1 block text-xs text-red-300">
                              {errors.whySelfLabs}
                            </span>
                          )}
                        </label>
                      </div>

                      <label className="mt-6 flex items-start gap-3 text-sm text-white/65">
                        <input
                          type="checkbox"
                          checked={application.consent}
                          onChange={(e) => updateField('consent', e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-indigo-500 focus:ring-indigo-400/40"
                        />
                        <span>
                          I agree to be contacted by SELF Labs Inc. about this fellowship application,
                          understand the program is unpaid, and consent to my information being used
                          for recruiting purposes. *
                        </span>
                      </label>
                      {errors.consent && (
                        <span className="mt-1 block text-xs text-red-300">{errors.consent}</span>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3.5 text-sm font-semibold text-white transition hover:from-indigo-400 hover:to-purple-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Submitting…
                          </>
                        ) : (
                          'Apply to the Fellowship'
                        )}
                      </button>
                    </form>
                  </motion.div>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
